import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { ReactNode } from 'react';
import { toast } from 'sonner';
import {
  usePatient,
  useResendAppointmentConfirmation,
} from '../../services/httpServices/queries';
import type { Patient } from '../../types/patient';
import { formatDateLabel, formatDateWithWeekday, formatTimeRange } from '../../utils/dateFormat';
import { joinFullName } from '../../utils/patientName';
import { patientCategoryLabel } from '../../utils/patientCategory';
import {
  composePatientAddress,
  permanentAddressParts,
  presentAddressParts,
} from '../../utils/patientAddress';
import {
  X,
  Loader2,
  AlertCircle,
  User,
  Calendar,
  Clock,
  Users,
  Droplets,
  Phone,
  Mail,
  Stethoscope,
  MapPin,
  HeartPulse,
  ShieldAlert,
  Activity,
  FileText,
  ExternalLink,
  DoorOpen,
  Building2,
  Award,
  GraduationCap,
  Send,
  Banknote,
  Hash,
} from 'lucide-react';

interface AppointmentDetailsModalProps {
  /** The appointment row (with patient/doctor/department relations). */
  appointment: any;
  onClose: () => void;
}

const statusStyles: Record<string, string> = {
  SCHEDULED: 'bg-secondary/10 text-secondary',
  CHECKED_IN: 'bg-primary/10 text-primary',
  IN_PROGRESS: 'bg-warning/10 text-warning',
  COMPLETED: 'bg-success/10 text-success',
  CANCELLED: 'bg-danger/10 text-danger',
  NO_SHOW: 'bg-text-muted/10 text-text-muted',
};

/** Fee band stamped on the appointment when it was booked. */
const feeTypeLabels: Record<string, string> = {
  new: 'New patient',
  followup: 'Follow-up',
  report: 'Report check',
};

const medicalFlagDefs: { key: keyof Patient; label: string }[] = [
  { key: 'drugAllergy', label: 'Drug Allergy' },
  { key: 'foodAllergy', label: 'Food Allergy' },
  { key: 'pregnancy', label: 'Pregnancy' },
  { key: 'highRisk', label: 'High Risk' },
  { key: 'infectiousDisease', label: 'Infectious Disease' },
  { key: 'fallRisk', label: 'Fall Risk' },
  { key: 'wheelchair', label: 'Wheelchair' },
  { key: 'hearingImpairment', label: 'Hearing Impairment' },
  { key: 'visualImpairment', label: 'Visual Impairment' },
];

export function AppointmentDetailsModal({
  appointment,
  onClose,
}: AppointmentDetailsModalProps) {
  const patientId = appointment?.patientId || appointment?.patient?.id;
  const walkIn = !patientId;

  const { data, isLoading, error } = usePatient(patientId || '');
  const patient: Partial<Patient> =
    data?.data ??
    appointment?.patient ??
    (walkIn
      ? {
          firstName: appointment?.patientName || 'Walk-in patient',
          mobileNumber: appointment?.patientMobile,
        }
      : {});

  const fullName =
    joinFullName(patient) ||
    (walkIn ? 'Walk-in patient' : 'Unknown patient');
  const ageLabel = patient.age ? `${patient.age} years` : 'N/A';
  const dobLabel = patient.dateOfBirth
    ? formatDateLabel(patient.dateOfBirth)
    : 'N/A';

  const doctorName = appointment?.doctor?.name || 'Unknown';
  const doctorDesignation = appointment?.doctor?.designation?.name || 'N/A';
  const doctorSpecializations: string[] = (appointment?.doctor?.specializations || [])
    .map((specialization: any) => specialization?.label || specialization?.name)
    .filter((value: unknown): value is string => Boolean(value));
  const departmentName =
    appointment?.department?.name || appointment?.department?.code || 'N/A';
  const status = appointment?.status || 'SCHEDULED';
  const isWalkInAppointment = appointment?.isWalkIn ?? walkIn;

  const consultationFee =
    appointment?.consultationFee != null ? Number(appointment.consultationFee) : null;
  const consultationFeeLabel = feeTypeLabels[appointment?.consultationFeeType] || null;

  const room = appointment?.consultationRoom ?? appointment?.doctor?.consultationRoom;
  const roomName = room?.name || 'N/A';
  const floorLabel = room?.floor
    ? room.floor.name ||
      (room.floor.floorNumber && room.floor.floorNumber > 0
        ? String(room.floor.floorNumber)
        : 'N/A')
    : 'N/A';

  const medicalFlags = medicalFlagDefs.filter((flag) => Boolean(patient[flag.key]));
  const clinicalAlerts = patient.clinicalAlerts || [];

  const resendConfirmation = useResendAppointmentConfirmation();
  const [resending, setResending] = useState(false);

  const handleResendConfirmation = async () => {
    if (!appointment?.id) return;
    setResending(true);
    try {
      const response = await resendConfirmation.mutateAsync(appointment.id);
      if (response?.data?.sent) {
        toast.success('Appointment confirmation sent');
      } else {
        toast.error('Confirmation could not be sent. Check the patient phone number.');
      }
    } catch {
      toast.error('Failed to send appointment confirmation');
    } finally {
      setResending(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        className="bg-surface border border-border rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-3 p-4 border-b border-border">
          <div className="flex items-start gap-3 min-w-0">
            <div className="p-2 rounded-lg bg-primary/10 shrink-0">
              <User size={20} className="text-primary" />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="text-lg font-semibold text-text truncate">{fullName}</h2>
                <span
                  className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                    statusStyles[status] || 'bg-text-muted/10 text-text-muted'
                  }`}
                >
                  {status.replace(/_/g, ' ')}
                </span>
                {isWalkInAppointment && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-warning/10 text-warning font-medium">
                    Walk-in
                  </span>
                )}
              </div>
              <p className="text-xs font-mono text-text-muted mt-0.5">
                {patient.registrationNumber || '—'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-background text-text-muted shrink-0"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="p-4 space-y-5 overflow-y-auto">
          {isLoading && !walkIn && (
            <div className="flex items-center gap-2 text-sm text-text-muted">
              <Loader2 size={14} className="animate-spin" /> Loading full details…
            </div>
          )}

          {error && !patient.firstName && (
            <div className="flex items-center gap-2 p-3 bg-danger/10 border border-danger/20 rounded-lg text-danger text-sm">
              <AlertCircle size={16} />
              <span>Failed to load patient details.</span>
            </div>
          )}

          {/* Appointment Details */}
          <div className="bg-background border border-border rounded-xl p-4 space-y-3">
            <div className="flex items-center gap-2">
              <Calendar size={16} className="text-primary" />
              <h3 className="text-sm font-semibold text-text">Appointment Details</h3>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              <InfoTile
                icon={<Calendar size={16} className="text-primary" />}
                label="Date"
                value={formatDateWithWeekday(appointment?.date)}
              />
              <InfoTile
                icon={<Clock size={16} className="text-primary" />}
                label="Time"
                value={formatTimeRange(appointment?.time, appointment?.endTime) || 'N/A'}
              />
              <InfoTile
                icon={<Hash size={16} className="text-primary" />}
                label="Serial No."
                value={
                  appointment?.serialNumber
                    ? `#${appointment.serialNumber}${
                        appointment?.session ? ` · ${appointment.session}` : ''
                      }`
                    : 'N/A'
                }
              />
              <InfoTile
                icon={<Stethoscope size={16} className="text-primary" />}
                label="Doctor"
                value={doctorName}
              />
              <InfoTile
                icon={<Award size={16} className="text-primary" />}
                label="Designation"
                value={doctorDesignation}
              />
              <InfoTile
                icon={<GraduationCap size={16} className="text-primary" />}
                label="Specializations"
                value={
                  doctorSpecializations.length > 0 ? (
                    <div className="flex flex-wrap gap-1.5">
                      {doctorSpecializations.map((specialization) => (
                        <span
                          key={specialization}
                          className="inline-flex items-center px-2 py-0.5 rounded-full bg-primary/10 text-primary text-xs font-medium"
                        >
                          {specialization}
                        </span>
                      ))}
                    </div>
                  ) : (
                    'N/A'
                  )
                }
              />
              <InfoTile
                icon={<Activity size={16} className="text-primary" />}
                label="Department"
                value={departmentName}
              />
              <InfoTile
                icon={<DoorOpen size={16} className="text-primary" />}
                label="Room"
                value={roomName}
              />
              <InfoTile
                icon={<Building2 size={16} className="text-primary" />}
                label="Floor"
                value={floorLabel}
              />
              <InfoTile
                icon={<Calendar size={16} className="text-primary" />}
                label="Status"
                value={<StatusBadge status={status} />}
              />
              <InfoTile
                icon={<User size={16} className="text-primary" />}
                label="Type"
                value={
                  isWalkInAppointment
                    ? 'Walk-in'
                    : appointment?.appointmentType || 'Scheduled'
                }
              />
              <InfoTile
                icon={<Banknote size={16} className="text-primary" />}
                label="Consultation Fee"
                value={
                  consultationFee != null ? (
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium text-text">
                        ৳{consultationFee.toLocaleString()}
                      </span>
                      {consultationFeeLabel && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-primary/10 text-primary text-[11px] font-medium">
                          {consultationFeeLabel}
                        </span>
                      )}
                    </div>
                  ) : (
                    'N/A'
                  )
                }
              />
            </div>

            {appointment?.notes && (
              <div className="pt-1">
                <p className="text-xs text-text-muted mb-1">Notes</p>
                <p className="text-sm text-text whitespace-pre-wrap">{appointment.notes}</p>
              </div>
            )}
          </div>

          {/* Patient Details */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <InfoTile
              icon={<Calendar size={16} className="text-primary" />}
              label="Age / DOB"
              value={patient.age ? `${ageLabel} (${dobLabel})` : dobLabel}
            />
            <InfoTile
              icon={<Users size={16} className="text-primary" />}
              label="Gender"
              value={patient.gender || 'N/A'}
              capitalize
            />
            <InfoTile
              icon={<Droplets size={16} className="text-primary" />}
              label="Blood Group"
              value={patient.bloodGroup || 'N/A'}
            />
            <InfoTile
              icon={<Phone size={16} className="text-primary" />}
              label="Mobile"
              value={patient.mobileNumber || 'N/A'}
            />
            <InfoTile
              icon={<Mail size={16} className="text-primary" />}
              label="Email"
              value={patient.email || 'N/A'}
            />
            <InfoTile
              icon={<Stethoscope size={16} className="text-primary" />}
              label="Category"
              value={patient.patientCategory ? patientCategoryLabel(patient.patientCategory) : 'N/A'}
            />
          </div>

          {!walkIn && (
            <>
              {/* Address & emergency */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className="bg-background border border-border rounded-xl p-4 space-y-3">
                  <div className="flex items-center gap-2">
                    <MapPin size={16} className="text-primary" />
                    <h3 className="text-sm font-semibold text-text">Address</h3>
                  </div>
                  <DetailRow
                    label="Permanent"
                    value={composePatientAddress(permanentAddressParts(patient)) || 'N/A'}
                  />
                  <DetailRow
                    label="Present"
                    value={composePatientAddress(presentAddressParts(patient)) || 'N/A'}
                  />
                </div>

                <div className="bg-background border border-border rounded-xl p-4 space-y-3">
                  <div className="flex items-center gap-2">
                    <HeartPulse size={16} className="text-primary" />
                    <h3 className="text-sm font-semibold text-text">Emergency Contact</h3>
                  </div>
                  <DetailRow label="Name" value={patient.emergencyContactName || 'N/A'} />
                  <DetailRow label="Relation" value={patient.emergencyContactRelation || 'N/A'} />
                  <DetailRow label="Phone" value={patient.emergencyContactPhone || 'N/A'} />
                </div>
              </div>

              {/* Medical flags */}
              <div className="bg-background border border-border rounded-xl p-4 space-y-3">
                <div className="flex items-center gap-2">
                  <Activity size={16} className="text-primary" />
                  <h3 className="text-sm font-semibold text-text">Medical Flags</h3>
                </div>
                {medicalFlags.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {medicalFlags.map((flag) => (
                      <span
                        key={String(flag.key)}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-danger/10 text-danger border border-danger/20 text-xs font-medium"
                      >
                        <ShieldAlert size={12} />
                        {flag.label}
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-text-muted">No active medical flags.</p>
                )}
              </div>

              {/* Clinical alerts */}
              {clinicalAlerts.length > 0 && (
                <div className="bg-background border border-border rounded-xl p-4 space-y-3">
                  <div className="flex items-center gap-2">
                    <ShieldAlert size={16} className="text-primary" />
                    <h3 className="text-sm font-semibold text-text">Clinical Alerts</h3>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {clinicalAlerts.map((alert) => (
                      <span
                        key={alert.id}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-warning/10 text-warning border border-warning/20 text-xs font-medium"
                      >
                        <ShieldAlert size={12} />
                        {alert.clinicalAlert?.label || alert.clinicalAlert?.name || 'Alert'}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Medical note */}
              {patient.medicalNote && (
                <div className="bg-background border border-border rounded-xl p-4 space-y-2">
                  <div className="flex items-center gap-2">
                    <FileText size={16} className="text-text-muted" />
                    <h3 className="text-sm font-semibold text-text">Medical Note</h3>
                  </div>
                  <p className="text-sm text-text-muted whitespace-pre-wrap">{patient.medicalNote}</p>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 p-4 border-t border-border">
          <button
            onClick={handleResendConfirmation}
            disabled={resending}
            className="inline-flex items-center gap-2 px-4 py-2 bg-secondary/10 text-secondary rounded-lg hover:bg-secondary/20 transition-colors text-sm font-medium disabled:opacity-50"
          >
            {resending ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              <Send size={16} />
            )}
            Send Confirmation Again
          </button>
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm text-text-muted hover:text-text transition-colors"
            >
              Close
            </button>
            {patientId && (
              <Link
                to={`/patients/${patientId}`}
                className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium"
              >
                <ExternalLink size={16} />
                View Full Profile
              </Link>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  return (
    <span
      className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
        statusStyles[status] || 'bg-text-muted/10 text-text-muted'
      }`}
    >
      {status.replace(/_/g, ' ')}
    </span>
  );
}

function InfoTile({
  icon,
  label,
  value,
  capitalize,
}: {
  icon: ReactNode;
  label: string;
  value: ReactNode;
  capitalize?: boolean;
}) {
  return (
    <div className="flex items-center gap-3 bg-surface rounded-lg p-3 border border-border">
      <div className="p-2 bg-primary/10 rounded-lg shrink-0">{icon}</div>
      <div className="min-w-0">
        <p className="text-xs text-text-muted">{label}</p>
        {typeof value === 'string' ? (
          <p className={`text-sm font-medium text-text break-words ${capitalize ? 'capitalize' : ''}`}>
            {value}
          </p>
        ) : (
          <div className="mt-0.5">{value}</div>
        )}
      </div>
    </div>
  );
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-start justify-between gap-3 py-1.5 border-b border-border last:border-0">
      <span className="text-xs text-text-muted shrink-0">{label}</span>
      <span className="text-sm font-medium text-text text-right break-words">{value}</span>
    </div>
  );
}
